import { Auth } from "aws-amplify"
import {
  AgentStatusResponse,
  AgentCreateResponse,
  AgentConnectResponse,
  AgentDisconnectResponse,
  AgentToggleResponse,
  AgentDeleteResponse,
  AgentDetailedStatusResponse,
  agentExists,
} from "./DistilleryTypes"

// Base URL for the Distillery API
const DISTILLERY_BASE_URL = process.env.REACT_APP_DISTILLERY_API_URL || ""

const DistilleryService = {
  // Helper method to get authorization headers
  async getAuthHeaders(): Promise<{ Authorization: string; "Content-Type": string }> {
    const session = await Auth.currentSession()
    return {
      Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
      "Content-Type": "application/json",
    }
  },

  // Helper method for API calls with authentication
  async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${DISTILLERY_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }

    return response.json()
  },

  // ===== Agent Management Endpoints =====

  /**
   * Check if an agent exists for the organization
   */
  async getAgentStatus(): Promise<AgentStatusResponse> {
    return this.apiCall<AgentStatusResponse>("/v1/zeef-agents/")
  },

  /**
   * Create a bot agent for the organization
   * This is idempotent-safe (returns error if agent already exists)
   * @param userType - The user type ID to assign to the agent
   */
  async createAgent(userType: string): Promise<AgentCreateResponse> {
    return this.apiCall<AgentCreateResponse>("/v1/zeef-agents/", {
      method: "POST",
      body: JSON.stringify({ user_type: userType }),
    })
  },

  /**
   * Connect the agent to ZEEF's WebSocket service
   * The agent will start responding to messages and follow events
   */
  async connectAgent(): Promise<AgentConnectResponse> {
    return this.apiCall<AgentConnectResponse>("/v1/zeef-agents/connect", {
      method: "POST",
    })
  },

  /**
   * Disconnect the agent from ZEEF
   * The agent stops responding but remains configured
   */
  async disconnectAgent(): Promise<AgentDisconnectResponse> {
    return this.apiCall<AgentDisconnectResponse>("/v1/zeef-agents/disconnect", {
      method: "POST",
    })
  },

  /**
   * Toggle the agent's active state
   * Disabled agents won't auto-reconnect on Distillery restart
   */
  async toggleAgent(): Promise<AgentToggleResponse> {
    return this.apiCall<AgentToggleResponse>("/v1/zeef-agents/toggle", {
      method: "POST",
    })
  },

  /**
   * Permanently delete the agent and its Cognito user
   */
  async deleteAgent(): Promise<AgentDeleteResponse> {
    return this.apiCall<AgentDeleteResponse>("/v1/zeef-agents/", {
      method: "DELETE",
    })
  },

  /**
   * Get detailed real-time connection status including live WebSocket state
   */
  async getDetailedStatus(): Promise<AgentDetailedStatusResponse> {
    return this.apiCall<AgentDetailedStatusResponse>("/v1/zeef-agents/status")
  },

  // ===== Automatic Agent Management =====

  /**
   * Ensures an agent exists and is connected for the organization.
   * This implements the recommended flow from the Distillery documentation:
   * 1. Check if agent exists
   * 2. Create agent if needed
   * 3. Connect agent if not already connected
   *
   * @param userType The user type ID to use if agent needs to be created
   * @returns Promise resolving to the agent status after ensuring connection
   */
  async ensureAgentConnected(userType: string): Promise<AgentDetailedStatusResponse> {
    // 1. Check if agent exists
    const status = await this.getAgentStatus()

    if (!agentExists(status)) {
      // 2. Create agent (first time only)
      await this.createAgent(userType)
    }

    // 3. Connect agent to ZEEF (if not already connected)
    if (!agentExists(status) || status.connection_status !== "connected") {
      await this.connectAgent()
    }

    // Return the current detailed status
    return this.getDetailedStatus()
  },

  /**
   * Verifies the agent connection and reconnects if necessary.
   * Use this to verify the agent is connected after app startup.
   *
   * @returns Promise resolving to the agent status
   */
  async verifyAgentConnection(): Promise<AgentDetailedStatusResponse> {
    const status = await this.getDetailedStatus()

    if (status.exists && status.is_active && !status.is_actually_connected) {
      // Agent should be connected but isn't - reconnect it
      await this.connectAgent()
      return this.getDetailedStatus()
    }

    return status
  },

  /**
   * Connect with retry logic for resilient connection handling
   *
   * @param userType The user type ID to use if agent needs to be created
   * @param maxRetries Maximum number of retry attempts (default: 3)
   * @returns Promise resolving to true if connection succeeded
   */
  async connectWithRetry(userType: string, maxRetries = 3): Promise<boolean> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const connectRes = await fetch(`${DISTILLERY_BASE_URL}/v1/zeef-agents/connect`, {
          method: "POST",
          headers: await this.getAuthHeaders(),
        })

        if (connectRes.ok) {
          return true
        }

        if (connectRes.status === 404) {
          // Agent doesn't exist - create it first
          await this.createAgent(userType)
          continue // Retry connect
        }

        throw new Error(`Connect failed: ${connectRes.status}`)
      } catch (err) {
        if (i === maxRetries - 1) {
          throw err
        }
        // Exponential backoff
        await new Promise((r) => setTimeout(r, 1000 * (i + 1)))
      }
    }
    return false
  },
}

export default DistilleryService
